import { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { apiClient } from '../../api/client';

interface QRScannerPanelProps {
  onSwitchToLookup?: () => void;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '16px',
    paddingBottom: '80px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#fff',
    margin: '0 0 12px 0',
  },
  subtitle: {
    fontSize: '13px',
    color: '#a0b8a8',
    marginBottom: '16px',
  },
  scannerBox: {
    width: '100%',
    maxWidth: '320px',
    margin: '0 auto 16px',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '2px solid #c9a84c',
  },
  statusCard: {
    padding: '16px',
    borderRadius: '10px',
    textAlign: 'center' as const,
    marginBottom: '12px',
  },
  successCard: {
    backgroundColor: '#1a4a2e',
    border: '1px solid #2e7d32',
  },
  errorCard: {
    backgroundColor: '#3a1a1a',
    border: '1px solid #d32f2f',
  },
  statusIcon: {
    fontSize: '32px',
    marginBottom: '8px',
  },
  statusText: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#fff',
  },
  statusDetail: {
    fontSize: '12px',
    color: '#a0b8a8',
    marginTop: '4px',
  },
  startBtn: {
    display: 'block',
    width: '100%',
    padding: '16px',
    fontSize: '16px',
    fontWeight: 700,
    backgroundColor: '#c9a84c',
    color: '#0d2418',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    marginBottom: '12px',
  },
  stopBtn: {
    display: 'block',
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    fontWeight: 600,
    backgroundColor: '#2a4a3a',
    color: '#c9a84c',
    border: '1px solid #3a5a4a',
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '12px',
  },
  instructions: {
    fontSize: '12px',
    color: '#7a9a8a',
    textAlign: 'center' as const,
    marginTop: '12px',
    lineHeight: 1.5,
  },
};

export function QrScannerPanel({ onSwitchToLookup: _onSwitchToLookup }: QRScannerPanelProps) {
  const [scanning, setScanning] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [lastResult, setLastResult] = useState<{ success: boolean; message: string; detail?: string; guestRecordId?: string; alreadyCheckedIn?: boolean } | null>(null);
  const [location, setLocation] = useState<string | null>(() => {
    try { return localStorage.getItem('qrScanLocation'); } catch { return null; }
  });
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerDivId = 'qr-reader';

  const LOCATIONS = [
    { key: 'Hotel', icon: '🏨', label: 'Hotel' },
    { key: 'Symposium', icon: '🎓', label: 'Symposium' },
    { key: 'Meet_and_Greet', icon: '🤝', label: 'Meet & Greet' },
    { key: 'Ball_Door', icon: '🚪', label: 'Ball Door' },
  ];

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (scannerRef.current?.isScanning) {
        scannerRef.current.stop().catch(() => {});
      }
    };
  }, []);

  async function startScanning() {
    setLastResult(null);
    setScanning(true);

    // First, explicitly request camera permission to trigger the browser prompt
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      // Immediately stop — we just needed the permission grant
      stream.getTracks().forEach((track) => track.stop());
    } catch (permErr: any) {
      // Permission denied or not available
      setScanning(false);
      const isNotAllowed = permErr?.name === 'NotAllowedError' || permErr?.name === 'PermissionDeniedError';
      setLastResult({
        success: false,
        message: isNotAllowed ? 'Camera Permission Denied' : 'Camera Not Available',
        detail: isNotAllowed
          ? 'Your browser blocked camera access. On iPhone: go to Settings → Safari → Camera → Allow. Then reload this page. Or use "Upload QR Image" below.'
          : `Camera error: ${permErr?.message || 'unknown'}. Try the "Upload QR Image" option below.`,
      });
      return;
    }

    // Permission granted — now start html5-qrcode
    try {
      const scanner = new Html5Qrcode(scannerDivId);
      scannerRef.current = scanner;

      try {
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          handleQRCode,
          () => {},
        );
      } catch {
        // Fallback: try front camera
        try {
          await scanner.start(
            { facingMode: 'user' },
            { fps: 10, qrbox: { width: 250, height: 250 } },
            handleQRCode,
            () => {},
          );
        } catch (fallbackErr: any) {
          setScanning(false);
          setLastResult({
            success: false,
            message: 'Camera failed to start',
            detail: `${fallbackErr?.message || 'Unknown error'}. Try using "Upload QR Image" instead.`,
          });
        }
      }
    } catch (err: any) {
      setScanning(false);
      setLastResult({
        success: false,
        message: 'Scanner initialization failed',
        detail: err?.message || 'Unknown error',
      });
    }
  }

  async function stopScanning() {
    if (scannerRef.current?.isScanning) {
      await scannerRef.current.stop().catch(() => {});
    }
    setScanning(false);
  }

  async function handleQRCode(decodedText: string) {
    if (processing) return;
    setProcessing(true);

    // Stop scanning while we process
    if (scannerRef.current?.isScanning) {
      await scannerRef.current.stop().catch(() => {});
    }
    setScanning(false);

    try {
      // Parse the QR payload: { "type": "guest", "identifier": "<registrationId>" }
      let registrationId: string;
      try {
        const payload = JSON.parse(decodedText);
        if (payload.type !== 'guest' || !payload.identifier) {
          setLastResult({ success: false, message: 'Invalid QR code', detail: 'Not a valid guest check-in QR code.' });
          return;
        }
        registrationId = payload.identifier;
      } catch {
        setLastResult({ success: false, message: 'Invalid QR code', detail: 'Could not read the QR data.' });
        return;
      }

      // Find the guest record by registration ID (search supports UUID lookups)
      const searchResults = await apiClient<{ results: Array<{ guestRecordId: string; fullName: string; isCheckedIn: boolean }> }>(`/packet-pickup/search?q=${encodeURIComponent(registrationId)}`).catch(() => ({ results: [] }));

      if (!searchResults.results || searchResults.results.length === 0) {
        setLastResult({ success: false, message: 'Guest not found', detail: `No guest record found for this QR code (${registrationId.slice(0, 8)}...). They may need to be imported/provisioned first.` });
        return;
      }

      const guest = searchResults.results[0];

      // Check if already checked in
      if (guest.isCheckedIn) {
        setLastResult({
          success: true,
          message: `✓ ${guest.fullName} — already checked in`,
          detail: 'This guest was previously checked in.',
          guestRecordId: guest.guestRecordId,
          alreadyCheckedIn: true,
        });
        return;
      }

      // Process the packet pickup / check-in
      const pickupResult = await apiClient<{ guestName: string; ticketNumber: string | null; pickupLocation: string }>('/packet-pickup', {
        method: 'POST',
        body: { guestRecordId: guest.guestRecordId, pickupLocation: location },
      });

      // Enqueue to delivery queue (fire-and-forget — don't block check-in on failure)
      apiClient('/packet-pickup/queue', {
        method: 'POST',
        body: {
          guestName: pickupResult.guestName || guest.fullName,
          ticketNumber: pickupResult.ticketNumber || null,
          orderNumber: null,
          location: location || 'Unknown',
        },
      }).catch(() => { /* silent — queue failure doesn't affect check-in */ });

      setLastResult({
        success: true,
        message: `✓ ${guest.fullName} checked in!`,
        detail: `Location: ${location?.replace(/_/g, ' ') || 'Unknown'}`,
      });
    } catch (err: any) {
      const msg = err?.message || 'Check-in failed';
      // Handle "already processed" gracefully
      if (msg.toLowerCase().includes('already')) {
        setLastResult({ success: true, message: '✓ Already checked in', detail: msg });
      } else {
        setLastResult({ success: false, message: 'Check-in failed', detail: msg });
      }
    } finally {
      setProcessing(false);
    }
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>📷 Scan QR Code</h2>
      <p style={styles.subtitle}>
        Select your location, then scan the guest's QR code
      </p>

      {/* Location selector */}
      {!location && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#c9a84c', marginBottom: '10px' }}>Select your location:</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {LOCATIONS.map((loc) => (
              <button
                key={loc.key}
                type="button"
                onClick={() => {
                  setLocation(loc.key);
                  try { localStorage.setItem('qrScanLocation', loc.key); } catch {}
                }}
                style={{
                  padding: '16px 12px',
                  borderRadius: '10px',
                  border: '1px solid #3a5a4a',
                  backgroundColor: '#1a2e23',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>{loc.icon}</div>
                {loc.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Current location indicator */}
      {location && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', padding: '10px 14px', backgroundColor: '#1a2e23', borderRadius: '8px', border: '1px solid #3a5a4a' }}>
          <span style={{ fontSize: '13px', color: '#a0b8a8' }}>
            📍 Location: <strong style={{ color: '#c9a84c' }}>{LOCATIONS.find((l) => l.key === location)?.label || location}</strong>
          </span>
          <button type="button" onClick={() => { setLocation(null); try { localStorage.removeItem('qrScanLocation'); } catch {} }} style={{ background: 'none', border: 'none', color: '#888', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
        </div>
      )}

      {/* Status feedback */}
      {lastResult && (
        <div style={{ ...styles.statusCard, ...(lastResult.success ? styles.successCard : styles.errorCard) }}>
          <div style={styles.statusIcon}>{lastResult.success ? '✅' : '❌'}</div>
          <div style={styles.statusText}>{lastResult.message}</div>
          {lastResult.detail && <div style={styles.statusDetail}>{lastResult.detail}</div>}
          {lastResult.alreadyCheckedIn && lastResult.guestRecordId && (
            <div style={{ marginTop: '12px', display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button type="button" style={{ padding: '8px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: '#d32f2f', color: '#fff' }} onClick={async () => {
                try {
                  const res = await apiClient<{ message: string }>('/packet-pickup/reverse', { method: 'POST', body: { guestRecordId: lastResult.guestRecordId, reverseCheckIn: true, reversePickup: true } });
                  setLastResult({ success: true, message: res.message || 'Reversed', detail: 'Check-in and packet pickup have been undone.' });
                } catch (err: any) { setLastResult({ success: false, message: 'Reverse failed', detail: err?.message }); }
              }}>Undo Both</button>
              <button type="button" style={{ padding: '8px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: '#e65100', color: '#fff' }} onClick={async () => {
                try {
                  const res = await apiClient<{ message: string }>('/packet-pickup/reverse', { method: 'POST', body: { guestRecordId: lastResult.guestRecordId, reverseCheckIn: true, reversePickup: false } });
                  setLastResult({ success: true, message: res.message || 'Reversed', detail: 'Check-in reversed (packet pickup kept).' });
                } catch (err: any) { setLastResult({ success: false, message: 'Reverse failed', detail: err?.message }); }
              }}>Undo Check-In Only</button>
              <button type="button" style={{ padding: '8px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: '#7b1fa2', color: '#fff' }} onClick={async () => {
                try {
                  const res = await apiClient<{ message: string }>('/packet-pickup/reverse', { method: 'POST', body: { guestRecordId: lastResult.guestRecordId, reverseCheckIn: false, reversePickup: true } });
                  setLastResult({ success: true, message: res.message || 'Reversed', detail: 'Packet pickup reversed (check-in kept).' });
                } catch (err: any) { setLastResult({ success: false, message: 'Reverse failed', detail: err?.message }); }
              }}>Undo Pickup Only</button>
            </div>
          )}
        </div>
      )}

      {/* Scanner viewport */}
      {scanning && (
        <div style={styles.scannerBox}>
          <div id={scannerDivId} />
        </div>
      )}

      {/* Controls */}
      {!scanning && location && (
        <button
          type="button"
          style={styles.startBtn}
          onClick={startScanning}
          disabled={processing}
        >
          {processing ? 'Processing...' : lastResult ? '📷 Scan Another' : '📷 Start Camera'}
        </button>
      )}

      {scanning && (
        <button type="button" style={styles.stopBtn} onClick={stopScanning}>
          Stop Camera
        </button>
      )}

      {/* File upload fallback */}
      {!scanning && location && (
        <label style={{
          display: 'block',
          width: '100%',
          padding: '14px',
          fontSize: '15px',
          fontWeight: 600,
          backgroundColor: '#2a4a3a',
          color: '#c9a84c',
          border: '1px solid #3a5a4a',
          borderRadius: '10px',
          cursor: 'pointer',
          textAlign: 'center' as const,
          marginBottom: '12px',
        }}>
          📁 Upload QR Image Instead
          <input
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setProcessing(true);
              setLastResult(null);
              try {
                const scanner = new Html5Qrcode('qr-file-reader');
                const decodedText = await scanner.scanFile(file, true);
                await scanner.clear();
                await handleQRCode(decodedText);
              } catch {
                setLastResult({
                  success: false,
                  message: 'Could not read QR code',
                  detail: 'Make sure the image clearly shows the QR code.',
                });
                setProcessing(false);
              }
              // Reset file input
              e.target.value = '';
            }}
            disabled={processing}
          />
        </label>
      )}
      <div id="qr-file-reader" style={{ display: 'none' }} />

      {/* USB Scanner input — captures barcode scanner keyboard output */}
      {location && (
        <div style={{ marginTop: '12px', marginBottom: '12px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#c9a84c', marginBottom: '6px' }}>🔌 USB / Bluetooth Scanner</div>
          <input
            type="text"
            placeholder="Click here, then scan QR with hardware scanner..."
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '15px',
              backgroundColor: '#1a2e23',
              color: '#fff',
              border: '1px solid #3a5a4a',
              borderRadius: '10px',
              outline: 'none',
              boxSizing: 'border-box',
            }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const input = e.currentTarget;
                const val = input.value.trim();
                input.value = '';
                if (val && !processing) {
                  await handleQRCode(val);
                }
              }
            }}
            disabled={processing}
            aria-label="USB scanner input"
          />
          <div style={{ fontSize: '11px', color: '#6a8a7a', marginTop: '4px' }}>
            Tap the field, then use your USB/Bluetooth barcode scanner. It types the QR data and auto-submits.
          </div>
        </div>
      )}

      <p style={styles.instructions}>
        The guest shows their QR code from My Weekend → Check-In QR Code card.
        <br />
        Use the camera, upload a screenshot, or plug in a USB barcode scanner.
      </p>
    </div>
  );
}
